interface InterviewSession {
  id: string;
  ended_at: string;
  ai_evaluation: {
    communication?: number;
    confidence?: number;
    problemSolving?: number;
    technicalKnowledge?: number;
    professionalism?: number;
    experience?: number;
    overall_score?: number;
    finalScore?: number;
    detailedFeedback?: string;
    feedback?: string;
  };
  transcript?: string;
  responses?: any;
}

interface AggregatedMetrics {
  overallScore: number;
  contentScore: number;
  behaviourScore: number;
  communication: number;
  confidence: number;
  problemSolving: number;
  technicalKnowledge: number;
  professionalism: number;
  experience: number;
  grade: string;
  totalInterviews: number;
  improvementPercentage: number;
}

interface WeeklyTrend {
  week: string;
  contentScore: number;
  behaviourScore: number;
}

interface RadarData {
  structure: number;
  technicalDepth: number;
  clarity: number;
  relevance: number;
  detail: number;
}

interface BehaviourData {
  eyeContact: number;
  professionalism: number;
  pace: number;
  distractionFree: number;
}

interface ExtractedSkills {
  technical: string[];
  soft: string[];
}

interface CoachingHighlight {
  type: 'strength' | 'improvement';
  text: string;
}

export function calculateAggregatedMetrics(sessions: InterviewSession[]): AggregatedMetrics {
  if (!sessions || sessions.length === 0) {
    return {
      overallScore: 0,
      contentScore: 0,
      behaviourScore: 0,
      communication: 0,
      confidence: 0,
      problemSolving: 0,
      technicalKnowledge: 0,
      professionalism: 0,
      experience: 0,
      grade: 'No Interviews',
      totalInterviews: 0,
      improvementPercentage: 0,
    };
  }

  let totalCommunication = 0;
  let totalConfidence = 0;
  let totalProblemSolving = 0;
  let totalTechnicalKnowledge = 0;
  let totalProfessionalism = 0;
  let totalExperience = 0;
  let count = 0;

  sessions.forEach((session) => {
    const evaluation = session.ai_evaluation;
    if (evaluation) {
      totalCommunication += evaluation.communication || 0;
      totalConfidence += evaluation.confidence || 0;
      totalProblemSolving += evaluation.problemSolving || 0;
      totalTechnicalKnowledge += evaluation.technicalKnowledge || 0;
      totalProfessionalism += evaluation.professionalism || 0;
      totalExperience += evaluation.experience || 0;
      count++;
    }
  });

  if (count === 0) {
    return {
      overallScore: 0,
      contentScore: 0,
      behaviourScore: 0,
      communication: 0,
      confidence: 0,
      problemSolving: 0,
      technicalKnowledge: 0,
      professionalism: 0,
      experience: 0,
      grade: 'No Interviews',
      totalInterviews: 0,
      improvementPercentage: 0,
    };
  }

  const communication = totalCommunication / count;
  const confidence = totalConfidence / count;
  const problemSolving = totalProblemSolving / count;
  const technicalKnowledge = totalTechnicalKnowledge / count;
  const professionalism = totalProfessionalism / count;
  const experience = totalExperience / count;

  const contentScore = Math.round((communication + problemSolving + technicalKnowledge + experience) / 4);
  const behaviourScore = Math.round((confidence + professionalism) / 2);
  const overallScore = Math.round((contentScore + behaviourScore) / 2);

  const grade = getGrade(overallScore);
  const improvementPercentage = calculateImprovement(sessions);

  return {
    overallScore,
    contentScore,
    behaviourScore,
    communication: Math.round(communication),
    confidence: Math.round(confidence),
    problemSolving: Math.round(problemSolving),
    technicalKnowledge: Math.round(technicalKnowledge),
    professionalism: Math.round(professionalism),
    experience: Math.round(experience),
    grade,
    totalInterviews: sessions.length,
    improvementPercentage,
  };
}

function getGrade(score: number): string {
  if (score >= 80) return 'Strong';
  if (score >= 60) return 'Developing';
  if (score >= 40) return 'Needs Work';
  return 'Beginner';
}

function calculateImprovement(sessions: InterviewSession[]): number {
  if (sessions.length < 2) return 0;

  const sortedSessions = [...sessions].sort(
    (a, b) => new Date(a.ended_at).getTime() - new Date(b.ended_at).getTime()
  );

  const firstSession = sortedSessions[0];
  const lastSession = sortedSessions[sortedSessions.length - 1];

  const firstScore = firstSession.ai_evaluation?.overall_score || firstSession.ai_evaluation?.finalScore || 0;
  const lastScore = lastSession.ai_evaluation?.overall_score || lastSession.ai_evaluation?.finalScore || 0;

  if (firstScore === 0) return 0;

  return Math.round(((lastScore - firstScore) / firstScore) * 100);
}

export function calculateWeeklyTrends(sessions: InterviewSession[]): WeeklyTrend[] {
  if (!sessions || sessions.length === 0) {
    return [];
  }

  const sessionsByWeek: { [key: string]: InterviewSession[] } = {};
  const now = new Date();

  sessions.forEach((session) => {
    const sessionDate = new Date(session.ended_at);
    const weekDiff = Math.floor((now.getTime() - sessionDate.getTime()) / (7 * 24 * 60 * 60 * 1000));

    if (weekDiff < 5) {
      const weekKey = `Week -${weekDiff}`;
      if (!sessionsByWeek[weekKey]) {
        sessionsByWeek[weekKey] = [];
      }
      sessionsByWeek[weekKey].push(session);
    }
  });

  const trends: WeeklyTrend[] = [];
  for (let i = 4; i >= 0; i--) {
    const weekKey = `Week -${i}`;
    const weekSessions = sessionsByWeek[weekKey] || [];

    if (weekSessions.length > 0) {
      const metrics = calculateAggregatedMetrics(weekSessions);
      trends.push({
        week: i === 0 ? 'This Week' : weekKey,
        contentScore: metrics.contentScore,
        behaviourScore: metrics.behaviourScore,
      });
    } else {
      trends.push({
        week: i === 0 ? 'This Week' : weekKey,
        contentScore: 0,
        behaviourScore: 0,
      });
    }
  }

  return trends;
}

export function calculateRadarData(sessions: InterviewSession[]): RadarData {
  if (!sessions || sessions.length === 0) {
    return {
      structure: 0,
      technicalDepth: 0,
      clarity: 0,
      relevance: 0,
      detail: 0,
    };
  }

  const metrics = calculateAggregatedMetrics(sessions);

  return {
    structure: Math.min(100, metrics.communication + 5),
    technicalDepth: metrics.technicalKnowledge,
    clarity: Math.min(100, metrics.communication + 2),
    relevance: metrics.experience,
    detail: metrics.problemSolving,
  };
}

export function calculateBehaviourData(sessions: InterviewSession[]): BehaviourData {
  if (!sessions || sessions.length === 0) {
    return {
      eyeContact: 0,
      professionalism: 0,
      pace: 0,
      distractionFree: 0,
    };
  }

  const metrics = calculateAggregatedMetrics(sessions);

  return {
    eyeContact: Math.max(0, metrics.confidence - 5),
    professionalism: metrics.professionalism,
    pace: Math.max(0, Math.min(100, metrics.communication - 10)),
    distractionFree: Math.min(100, metrics.professionalism + 5),
  };
}

export function extractSkills(latestSession: InterviewSession | null): ExtractedSkills {
  if (!latestSession || !latestSession.ai_evaluation) {
    return {
      technical: [],
      soft: [],
    };
  }

  const feedback = latestSession.ai_evaluation.detailedFeedback || latestSession.ai_evaluation.feedback || '';

  const technicalKeywords = [
    'Salesforce', 'Administration', 'Flows', 'Automation', 'Permission Sets',
    'Security', 'Reporting', 'Dashboards', 'API', 'Integration', 'JavaScript',
    'Python', 'React', 'Node.js', 'SQL', 'Database', 'Cloud', 'AWS', 'Azure'
  ];

  const softKeywords = [
    'Communication', 'Leadership', 'Problem Solving', 'Teamwork',
    'Stakeholder Management', 'Requirement Gathering', 'STAR', 'Presentation'
  ];

  const technical: string[] = [];
  const soft: string[] = [];

  technicalKeywords.forEach(keyword => {
    if (feedback.toLowerCase().includes(keyword.toLowerCase())) {
      technical.push(keyword);
    }
  });

  softKeywords.forEach(keyword => {
    if (feedback.toLowerCase().includes(keyword.toLowerCase())) {
      soft.push(keyword);
    }
  });

  return {
    technical: Array.from(new Set(technical)).slice(0, 6),
    soft: Array.from(new Set(soft)).slice(0, 4),
  };
}

export function extractCoachingHighlights(latestSession: InterviewSession | null): CoachingHighlight[] {
  if (!latestSession || !latestSession.ai_evaluation) {
    return [];
  }

  const feedback = latestSession.ai_evaluation.detailedFeedback || latestSession.ai_evaluation.feedback || '';
  const highlights: CoachingHighlight[] = [];

  const strengthKeywords = ['excellent', 'strong', 'great', 'good', 'well done', 'impressive', 'clear'];
  const improvementKeywords = ['improve', 'consider', 'could', 'should', 'try', 'practice', 'work on'];

  const sentences = feedback.split(/[.!?]+/).filter(s => s.trim().length > 20);

  sentences.forEach(sentence => {
    const lowerSentence = sentence.toLowerCase();
    const hasStrength = strengthKeywords.some(keyword => lowerSentence.includes(keyword));
    const hasImprovement = improvementKeywords.some(keyword => lowerSentence.includes(keyword));

    if (hasStrength && highlights.filter(h => h.type === 'strength').length < 2) {
      highlights.push({ type: 'strength', text: sentence.trim() });
    } else if (hasImprovement && highlights.filter(h => h.type === 'improvement').length < 2) {
      highlights.push({ type: 'improvement', text: sentence.trim() });
    }
  });

  return highlights;
}
